import { Routes } from "@angular/router";

/*
WWW Components
*/

/* Billing Components
 */
import { EquipmentComponent } from "./billing/components/equipment/equipment.component.mjs";
import { InvoicesComponent } from "./billing/components/invoices/invoices.component.mjs";
import { MakePaymentComponent } from "./billing/components/make-payment/make-payment.component.mjs";
import { ChangePlansComponent } from "./billing/components/my-services/change-plans/change-plans.component.mjs";
import { MyServicesComponent } from "./billing/components/my-services/my-services.component.mjs";
import { OverviewComponent } from "./billing/components/overview/overview.component.mjs";
import { PaymentMethodsComponent } from "./billing/components/payment-methods/payment-methods.component.mjs";
import { AllTicketsComponent } from "./billing/components/support/all-tickets/all-tickets.component.mjs";
import { SupportComponent } from "./billing/components/support/support.component.mjs";
import { TransactionsComponent } from "./billing/components/transactions/transactions.component.mjs";
import { BillingLayoutComponent } from "./billing/layout/billing-layout/billing-layout.component.mjs";

/* Order Components
 */
import { OrderDashboardComponent } from "./order/pages/dashboard/order-dashboard.component";

/* Admin Components
 */

/* Shared Components
 */
import { AdminDashboardComponent } from "./admin/admin-dashboard.component";
import { AssetListComponent } from "./admin/components/assets/asset-list/asset-list.component";
import { AssetViewComponent } from "./admin/components/assets/asset-view/asset-view.component";
import { CustomerCreateComponent } from "./admin/components/customers/customer-create/customer-create.component";
import { CustomerDueInvoicingComponent } from "./admin/components/customers/customer-due-invoicing/customer-due-invoicing.component";
import { CustomerDuePaymentProcessingComponent } from "./admin/components/customers/customer-due-payment-processing/customer-due-payment-processing.component";
import { CustomerEditComponent } from "./admin/components/customers/customer-edit/customer-edit.component";
import { CustomerViewComponent } from "./admin/components/customers/customer-view/customer-view.component";
import { CustomersComponent } from "./admin/components/customers/customers.component";
import { MassEmailComponent } from "./admin/components/customers/mass-email/mass-email.component";
import { InvoiceListComponent } from "./admin/components/financial/invoices/invoice-list/invoice-list.component";
import { InvoiceViewComponent } from "./admin/components/financial/invoices/invoice-view/invoice-view.component";
import { PaymentListComponent } from "./admin/components/financial/payments/payment-list/payment-list.component";
import { PaymentViewComponent } from "./admin/components/financial/payments/payment-view/payment-view.component";
import { NetworkComponent } from "./admin/components/network/network.component";
import { ConduitCreateComponent } from "./admin/components/outside-plant/conduit/conduit-create/conduit-create.component";
import { ConduitEditComponent } from "./admin/components/outside-plant/conduit/conduit-edit/conduit-edit.component";
import { ConduitListComponent } from "./admin/components/outside-plant/conduit/conduit-list/conduit-list.component";
import { ConduitViewComponent } from "./admin/components/outside-plant/conduit/conduit-view/conduit-view.component";
import { FiberCreateComponent } from "./admin/components/outside-plant/fiber/fiber-create/fiber-create.component";
import { FiberEditComponent } from "./admin/components/outside-plant/fiber/fiber-edit/fiber-edit.component";
import { FiberListComponent } from "./admin/components/outside-plant/fiber/fiber-list/fiber-list.component";
import { FiberViewComponent } from "./admin/components/outside-plant/fiber/fiber-view/fiber-view.component";
import { HandholeCreateComponent } from "./admin/components/outside-plant/handhole/handhole-create/handhole-create.component";
import { HandholeEditComponent } from "./admin/components/outside-plant/handhole/handhole-edit/handhole-edit.component";
import { HandholeListComponent } from "./admin/components/outside-plant/handhole/handhole-list/handhole-list.component";
import { HandholeViewComponent } from "./admin/components/outside-plant/handhole/handhole-view/handhole-view.component";
import { OutsidePlantComponent } from "./admin/components/outside-plant/outside-plant.component";
import { SettingsPermissionsComponent } from "./admin/components/settings/settings-permissions/settings-permissions.component";
import { SettingsPlatformUsersComponent } from "./admin/components/settings/settings-platform-users/settings-platform-users.component";
import { SubscriptionCreateComponent } from "./admin/components/subscriptions/subscription-create/subscription-create.component";
import { SubscriptionEditComponent } from "./admin/components/subscriptions/subscription-edit/subscription-edit.component";
import { SubscriptionListComponent } from "./admin/components/subscriptions/subscription-list/subscription-list.component";
import { SubscriptionViewComponent } from "./admin/components/subscriptions/subscription-view/subscription-view.component";
import { MapComponent } from "./admin/map/map.component";
import { LoginComponent } from "./shared/components/auth/login/login.component";
import { RegisterComponent } from "./shared/components/auth/register/register.component";
import { ResetPasswordComponent } from "./shared/components/auth/reset-password/reset-password.component";
import { AuthGuard, authGuard } from "./shared/guards/auth.guard";
import { LoginGuard } from "./shared/guards/login.guard";
import { LayoutComponent } from "./www/layout/layout.component.mjs";
import { InvoiceEditComponent } from "./admin/components/financial/invoices/invoice-edit/invoice-edit.component";

function getComponent(): any {
  const subdomain = extractSubdomain();
  if (subdomain === "order") {
    return OrderDashboardComponent;
  }
  if (subdomain === "billing") {
    return BillingLayoutComponent;
  }
  if (subdomain === "admin") {
    return AdminDashboardComponent;
  }
  return LayoutComponent;
}

function getAuthGuard(): any {
  const subdomain = extractSubdomain();
  if (subdomain === "order") {
    return AuthGuard;
  }
  if (subdomain === "billing") {
    return AuthGuard;
  }
  if (subdomain === "admin") {
    return AuthGuard;
  }
  return undefined;
}

function extractSubdomain(): string {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  return parts[0];
}

export const routes: Routes = [
  {
    path: "",
    component: getComponent(),
    children: [
      {
        path: "home",
        loadComponent: () =>
          import("./www/pages/home/home.component.mjs").then(
            (m) => m.HomeComponent
          ),
      },
      {
        path: "login",
        component: LoginComponent,
        canActivate: [LoginGuard]
      },
      {
        path: "register",
        component: RegisterComponent,
        canActivate: [LoginGuard]
      },
      {
        path: "reset",
        component: ResetPasswordComponent,
        canActivate: [LoginGuard]
      },
      {
        path: "order",
        data: { title: "Orders" },
        children: [
          { path: "", redirectTo: "/address", pathMatch: "full" }
        ],
      },
      {
        path: "billing",
        data: { title: "Dashboard" },
        canActivate: [authGuard],
        children: [
          {
            path: "",
            redirectTo: "/overview",
            pathMatch: "full",
          },
          {
            path: "overview",
            component: OverviewComponent,
            data: { title: "Overview" },
          },
          {
            path: "overview/:id",
            component: OverviewComponent,
            data: { title: "Overview" },
          },
          {
            path: "make-a-payment",
            component: MakePaymentComponent,
            data: { title: "Make a Payment" },
          },
          {
            path: "make-a-payment/:id",
            component: MakePaymentComponent,
            data: { title: "Make a Payment" },
          },
          {
            path: "methods",
            component: PaymentMethodsComponent,
            data: { title: "Billing and Payment" },
          },
          {
            path: "invoices",
            component: InvoicesComponent,
            data: { title: "Invoices" },
          },
          {
            path: "invoices/:id",
            component: InvoicesComponent,
            data: { title: "Invoices" },
          },
          {
            path: "transactions",
            component: TransactionsComponent,
            data: { title: "Transactions" },
          },
          {
            path: "my-services",
            component: MyServicesComponent,
            data: { title: "My Services" },
          },
          {
            path: "my-services/plans",
            component: ChangePlansComponent,
            data: { title: "Change Your Plan" },
          },
          {
            path: "equipment",
            component: EquipmentComponent,
            data: { title: "Equipment" },
          },
          {
            path: "support",
            component: SupportComponent,
            data: { title: "Support" },
          },
          {
            path: "all-tickets",
            component: AllTicketsComponent,
            data: { title: "Support - View Tickets" },
          },
        ],
      },
      {
        path: "admin",
        canActivate: [authGuard],
        children: [
          {
            path: "customers/new",
            component: CustomerCreateComponent,
            data: { title: "New Customer" },
          },
          {
            path: "customers/view/:id",
            component: CustomerViewComponent,
            data: { title: "Customer View" },
          },
          {
            path: "customers/invoiceable",
            component: CustomerDueInvoicingComponent,
            data: { title: "Customers Due Invoices" },
          },
          {
            path: "customers/payment-processing",
            component: CustomerDuePaymentProcessingComponent,
            data: { title: "Customers Due Payment Processing" },
          },
          {
            path: "customers/email",
            component: MassEmailComponent,
            data: { title: "Mass Communication" },
          },
          {
            path: "customers/edit/:id",
            component: CustomerEditComponent,
            data: { title: "Edit Customer" },
          },
          {
            path: "subscriptions/new",
            component: SubscriptionCreateComponent,
            data: { title: "New Subscription" },
          },
          {
            path: "subscriptions/edit/:id",
            component: SubscriptionEditComponent,
            data: { title: "Edit Subscription" },
          },
          {
            path: "subscriptions/view/:id",
            component: SubscriptionViewComponent,
            data: { title: "View Subscription" },
          },
          {
            path: "invoices/view/:id",
            component: InvoiceViewComponent,
            data: { title: "Invoice View" },
          },
          {
            path: "invoices/edit/:id",
            component: InvoiceEditComponent,
            data: { title: "Edit Invoice" },
          },
          {
            path: "assets/view/:id",
            component: AssetViewComponent,
            data: { title: "Asset View" },
          },
          {
            path: "payments/view/:id",
            component: PaymentViewComponent,
            data: { title: "Payment View" },
          },
          {
            path: "",
            component: MapComponent,
            data: { title: "Map" },
          },
          {
            path: "customers",
            component: CustomersComponent,
            data: { title: "Customers" },
          },
          {
            path: "invoices",
            component: InvoiceListComponent,
            data: { title: "Invoices" },
          },
          {
            path: "payments",
            component: PaymentListComponent,
            data: { title: "Payments" },
          },
          {
            path: "assets",
            component: AssetListComponent,
            data: { title: "Assets" },
          },
          {
            path: "subscriptions",
            component: SubscriptionListComponent,
            data: { title: "Subscriptions" },
          },
          {
            path: "outside-plant",
            component: OutsidePlantComponent,
            data: { title: "Outside Plant" },
            children: [
              {
                path: "outside-plant",
                redirectTo: "outside-plant/fiber/list",
                pathMatch: "full",
              },
              {
                path: "fiber/list",
                component: FiberListComponent,
                data: { title: "Fiber List" },
              },
              {
                path: "fiber/view/:id",
                component: FiberViewComponent,
                data: { title: "View Fiber" },
              },
              {
                path: "fiber/edit/:id",
                component: FiberEditComponent,
                data: { title: "Edit Fiber" },
              },
              {
                path: "fiber/create",
                component: FiberCreateComponent,
                data: { title: "Create Fiber" },
              },

              {
                path: "conduit/list",
                component: ConduitListComponent,
                data: { title: "Conduit List" },
              },
              {
                path: "conduit/view/:id",
                component: ConduitViewComponent,
                data: { title: "View Conduit" },
              },
              {
                path: "conduit/edit/:id",
                component: ConduitEditComponent,
                data: { title: "Edit Conduit" },
              },
              {
                path: "conduit/create",
                component: ConduitCreateComponent,
                data: { title: "Create Conduit" },
              },

              {
                path: "handhole/list",
                component: HandholeListComponent,
                data: { title: "Handhole List" },
              },
              {
                path: "handhole/view/:id",
                component: HandholeViewComponent,
                data: { title: "View Handhole" },
              },
              {
                path: "handhole/edit/:id",
                component: HandholeEditComponent,
                data: { title: "Edit Handhole" },
              },
              {
                path: "handhole/create",
                component: HandholeCreateComponent,
                data: { title: "Create Handhole" },
              },
            ],
          },
          {
            path: "network",
            component: NetworkComponent,
            data: { title: "Network" },
          },
          {
            path: "settings",
            canActivate: [authGuard],
            children: [
              {
                path: "users",
                component: SettingsPlatformUsersComponent,
                data: { title: "Platform Users" },
              },
              {
                path: "permissions",
                component: SettingsPermissionsComponent,
                data: { title: "Permissions" },
              }
            ]
          },
        ],
      },
      // Fallback Route
      {
        path: "**",
        redirectTo: "", // Redirect to the home page for unmatched routes
      },
      {
        path: "outside-plant",
        component: OutsidePlantComponent,
        data: { title: "Outside Plant" },
        children: [
          {
            path: "outside-plant",
            redirectTo: "outside-plant/fiber/list",
            pathMatch: "full",
          },
          {
            path: "fiber/list",
            component: FiberListComponent,
            data: { title: "Fiber List" },
          },
          {
            path: "fiber/view/:id",
            component: FiberViewComponent,
            data: { title: "View Fiber" },
          },
          {
            path: "fiber/edit/:id",
            component: FiberEditComponent,
            data: { title: "Edit Fiber" },
          },
          {
            path: "fiber/create",
            component: FiberCreateComponent,
            data: { title: "Create Fiber" },
          },

          {
            path: "conduit/list",
            component: ConduitListComponent,
            data: { title: "Conduit List" },
          },
          {
            path: "conduit/view/:id",
            component: ConduitViewComponent,
            data: { title: "View Conduit" },
          },
          {
            path: "conduit/edit/:id",
            component: ConduitEditComponent,
            data: { title: "Edit Conduit" },
          },
          {
            path: "conduit/create",
            component: ConduitCreateComponent,
            data: { title: "Create Conduit" },
          },

          {
            path: "handhole/list",
            component: HandholeListComponent,
            data: { title: "Handhole List" },
          },
          {
            path: "handhole/view/:id",
            component: HandholeViewComponent,
            data: { title: "View Handhole" },
          },
          {
            path: "handhole/edit/:id",
            component: HandholeEditComponent,
            data: { title: "Edit Handhole" },
          },
          {
            path: "handhole/create",
            component: HandholeCreateComponent,
            data: { title: "Create Handhole" },
          },
        ],
      },
      {
        path: "network",
        component: NetworkComponent,
        data: { title: "Network" },
      },
    ],
  },
];
